import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  useNeighborhoodBySlug,
  useNeighborhoodProducts,
  useTopStoresInNeighborhood,
  neighborhoodBySlugQueryOptions,
} from '@/hooks/neighborhoodPageHooks';
import ProductCard from '@/components/store/ProductCard';
import StoreCard from '@/components/StoreCard';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdSpace from '@/components/AdSpace';

export const Route = createFileRoute('/_regular/home/neighborhood/$neighborhoodSlug')({
  component: NeighborhoodPage,
  loader: ({ context: { queryClient }, params }) =>
    queryClient
      .ensureQueryData(neighborhoodBySlugQueryOptions(params.neighborhoodSlug))
      .catch(() => null),
  head: ({ loaderData: neighborhood }) => {
    if (!neighborhood) return {}
    const description =
      neighborhood.description || `اكتشف أفضل المحلات والمتاجر في حي ${neighborhood.name}`
    return {
      meta: [
        { title: `متاجر حي ${neighborhood.name} | سكيكيمة` },
        { name: 'description', content: description },
        { property: 'og:title', content: neighborhood.name },
        { property: 'og:description', content: description },
      ],
    }
  },
});

function NeighborhoodPage() {
  const { neighborhoodSlug } = Route.useParams();
  const { data: neighborhood, isLoading: neighborhoodLoading } = useNeighborhoodBySlug(neighborhoodSlug);
  const [sortBy, setSortBy] = useState('-created_at');
  const { ref, inView } = useInView();
  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: productsLoading,
  } = useNeighborhoodProducts(neighborhood?.id, sortBy);
  const { data: topStores = [], isLoading: storesLoading } = useTopStoresInNeighborhood(neighborhood?.id);
  const products = productsData?.pages.flatMap((page) => page.data) || [];

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (neighborhoodLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Skeleton className="h-8 w-64 mb-4" />
      </div>
    );
  }
  if (!neighborhood) {
    return (
      <div className="container mx-auto py-8 px-4 text-center">الحي غير موجود</div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{neighborhood.name}</h1>
        <p className="text-muted-foreground">
          {neighborhood.city?.name} – استكشف المنتجات والمتاجر في هذا الحي
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content – Products */}
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">ترتيب حسب:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[180px] flex-row-reverse justify-between">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  dir="rtl"
                  style={{ width: 'var(--radix-select-trigger-width)' }}
                  className="min-w-[var(--radix-select-trigger-width)]"
                >
                  <SelectItem value="-created_at">الأحدث</SelectItem>
                  <SelectItem value="-loves_count">الأكثر إعجاباً</SelectItem>
                  <SelectItem value="price">السعر: من الأقل</SelectItem>
                  <SelectItem value="-price">السعر: من الأعلى</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-muted-foreground">{products.length} منتج</span>
          </div>

          {productsLoading && products.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">لا توجد منتجات في هذا الحي بعد</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <div ref={ref} className="h-10 mt-4" />
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Sidebar – desktop */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="sticky top-20 space-y-6">
            <AdSpace variant="large" />
            <div className="bg-card rounded-lg border p-4">
              <h3 className="font-semibold mb-4">أفضل المتاجر في {neighborhood.name}</h3>
              {storesLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : topStores.length === 0 ? (
                <p className="text-sm text-muted-foreground">لا توجد متاجر بعد</p>
              ) : (
                <div className="space-y-3">
                  {topStores.map((store) => (
                    <StoreCard key={store.id} store={store} compact />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet inline blocks */}
      <div className="lg:hidden space-y-8 mt-8">
        <AdSpace variant="medium" />
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-4">أفضل المتاجر في {neighborhood.name}</h3>
          {storesLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : topStores.length === 0 ? (
            <p className="text-sm text-muted-foreground">لا توجد متاجر بعد</p>
          ) : (
            <div className="space-y-3">
              {topStores.map((store) => (
                <StoreCard key={store.id} store={store} compact />
              ))}
            </div>
          )}
        </div>
        <AdSpace variant="small" />
      </div>
    </div>
  );
}